import {Heading, Pane, Paragraph} from "evergreen-ui";
import React, {useContext, useState} from "react";
import {useOktaAuth} from "@okta/okta-react";
import {useApplicationIsInternalAuth} from "common/component/ApplicationRootContext";
import {ClientManagementLayout} from "../../layouts/ClientManagementLayout";
import {RosterUploadModal} from "./RosterUploadModal";
import {RosterUploadHistoryTable} from "./RosterUploadHistoryTable";
import {fetchRosterTemplateUrl} from "./domain/roster";
import {useCurrentUser} from "security";
import {OAuthManagerContext} from "security/component/azure/context";
import {PORTAL_NAVY} from "../../../common";

const UPLOAD_PINK = "#D00261";

// Tray-style upload glyph (up arrow over an open tray), matching the mockup's Upload Roster button.
// Sized to the button text height per the mockup.
const UploadTrayIcon = () => (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none"
         stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 16V4"/>
        <path d="M7 9l5-5 5 5"/>
        <path d="M5 14v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4"/>
    </svg>
);

// Microsoft Excel file icon for the Download Template link.
// Artwork from vscode-icons (https://github.com/vscode-icons/vscode-icons), MIT, (c) 2016 Roberto Huertas.
const ExcelIcon = () => (
    <svg width="24" height="24" viewBox="0 0 32 32" aria-hidden="true">
        <defs>
            <linearGradient id="rosterExcelGrad" x1="4.494" y1="-2092.086" x2="13.832" y2="-2075.914"
                            gradientTransform="translate(0 2100)" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#18884f"/>
                <stop offset="0.5" stopColor="#117e43"/>
                <stop offset="1" stopColor="#0b6631"/>
            </linearGradient>
        </defs>
        <path d="M19.581,15.35,8.512,13.4V27.809A1.192,1.192,0,0,0,9.705,29h19.1A1.192,1.192,0,0,0,30,27.809h0V22.5Z" fill="#185c37"/>
        <path d="M19.581,3H9.705A1.192,1.192,0,0,0,8.512,4.191h0V9.5L19.581,16l5.861,1.95L30,16V9.5Z" fill="#21a366"/>
        <path d="M8.512,9.5H19.581V16H8.512Z" fill="#107c41"/>
        <path d="M3.194,8.85H15.132a1.193,1.193,0,0,1,1.194,1.191V21.959a1.193,1.193,0,0,1-1.194,1.191H3.194A1.192,1.192,0,0,1,2,21.959V10.041A1.192,1.192,0,0,1,3.194,8.85Z" fill="url(#rosterExcelGrad)"/>
        <path d="M5.7,19.873l2.511-3.884-2.3-3.862H7.758L9.013,14.6c.116.234.2.408.238.524h.017c.082-.188.169-.369.26-.546l1.342-2.447h1.7l-2.359,3.84,2.419,3.905H10.821l-1.45-2.711A2.355,2.355,0,0,1,9.2,16.8H9.176a1.688,1.688,0,0,1-.168.351L7.515,19.873Z" fill="#fff"/>
        <path d="M28.806,3H19.581V9.5H30V4.191A1.192,1.192,0,0,0,28.806,3Z" fill="#33c481"/>
        <path d="M19.581,16H30v6.5H19.581Z" fill="#107c41"/>
    </svg>
);

export const RosterManagementPage = () => {
    const isInternalAuth = useApplicationIsInternalAuth();
    return isInternalAuth ? <AzureRosterManagementPage /> : <OktaRosterManagementPage />;
};

const AzureRosterManagementPage = () => {
    const oauthManager = useContext(OAuthManagerContext)!;
    return <RosterManagementPageContent getToken={() => oauthManager.jwtForApi()} isInternalAuth={true} />;
};

const OktaRosterManagementPage = () => {
    const {oktaAuth} = useOktaAuth();
    return <RosterManagementPageContent getToken={() => {
        const token = oktaAuth.authStateManager.getAuthState()?.accessToken?.accessToken;
        return Promise.resolve(token);
    }} isInternalAuth={false} />;
};

type RosterManagementPageContentProps = {
    getToken: () => Promise<string | undefined>;
    isInternalAuth?: boolean;
};

const RosterManagementPageContent = ({getToken, isInternalAuth = false}: RosterManagementPageContentProps) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    // Bumped after a successful upload to refetch the history table.
    const [refreshToken, setRefreshToken] = useState(0);

    const handleDownloadTemplate = async () => {
        try {
            const token = await getToken();
            const url = await fetchRosterTemplateUrl(token);
            // Open in a new tab (like every other S3 download in the app, e.g. ClientReportFileRowItem).
            // Each download in its own tab gets a fresh per-tab limiter, so Chrome won't block the 2nd
            // download on the page. The fetch resolves well within the ~5s transient-activation window,
            // so window.open is not popup-blocked. The presigned URL carries Content-Disposition:
            // attachment, so the new tab downloads and closes rather than navigating.
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            console.error("Failed to fetch roster template URL", err);
        }
    };

    const downloadTemplateLink = (
        <Pane is="button" type="button" onClick={handleDownloadTemplate}
              display="inline-flex" alignItems="center" cursor="pointer"
              background="none" border="none" padding={0}>
            <ExcelIcon/>
            <span className="text-blue-portal-link underline" style={{marginLeft: 8}}>Download Template</span>
        </Pane>
    );

    const uploadRosterButton = (
        isInternalAuth ? null : (
        <button type="button" onClick={() => setShowUploadModal(true)}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    height: 40,
                    padding: "0 20px",
                    marginRight: 16,
                    border: "none",
                    borderRadius: 6,
                    background: UPLOAD_PINK,
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                }}>
            <UploadTrayIcon/>
            Upload Roster
        </button>
        )
    );

    return (
        <ClientManagementLayout customSelection={'Roster Management'}>
            <Pane className="m-5 p-5 bg-white shadow-md rounded flex flex-col portal-type-scale" flexGrow={1}
                  minHeight="calc(100vh - 100px - 260px - 36px)">
                <Heading size={900} fontSize={32} color={PORTAL_NAVY} marginBottom={4}>Roster Management</Heading>
                <Paragraph size={500} color="muted" marginBottom={24}>
                    Upload your roster files and view upload history below.
                </Paragraph>
                <RosterUploadHistoryTable
                    refreshToken={refreshToken}
                    leftActions={downloadTemplateLink}
                    rightActions={uploadRosterButton}
                />
            </Pane>
            <RosterUploadModal
                isShown={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUploaded={() => setRefreshToken((token) => token + 1)}
                getToken={getToken}
            />
        </ClientManagementLayout>
    )
}
